import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import { AuthedImage } from '@/components/media/AuthedImage';

interface Checkpoint { key: string; label: string; location: string; at: string | null; reached: boolean; current: boolean }
interface Detail {
  id: string; reference: string; state: string; productTitle: string; brand: string; code?: string;
  buyer: { name: string; email: string }; seller: { name: string; email: string };
  amountGBP: number; totalGBP: number; carrier: string; trackingCode?: string; estimatedDelivery?: string | null;
  checkpoints: Checkpoint[];
  evidence: {
    serialPhoto?: string; imei?: string | null; dropoffPhoto?: string; collectionPhoto?: string;
    buyerDisputePhotos: string[]; sellerDisputePhotos: string[];
    disputeReason?: string | null; disputeNotes?: string | null; sellerResponse?: string | null;
  };
  events: { toState: string; source: string; note?: string; at: string }[];
  shippingAddress?: Addr | null;
  billingAddress?: Addr | null;
}
interface Addr { name?: string; line1?: string; line2?: string; city?: string; postcode?: string; country?: string; phone?: string }
const fmtAddr = (a?: Addr | null) => a ? [a.name, a.line1, a.line2, a.city, a.postcode, a.country].filter(Boolean).join(', ') : null;

// The lifecycle transitions an admin can drive, in order.
const STEPS: { to: string; label: string; from: string[] }[] = [
  { to: 'paid',      label: '1 · Mark payment approved', from: ['CREATED'] },
  { to: 'in_locker', label: '2 · Seller dropped / in transit', from: ['PAID_HELD', 'AWAITING_DROP', 'SHIPPED'] },
  { to: 'collected', label: '3 · Delivered / collected', from: ['IN_LOCKER'] },
  { to: 'release',   label: '4 · Clear funds to seller', from: ['COLLECTED', 'IN_LOCKER', 'DISPUTED'] },
];


export function AdminOrderDetail({ orderId, onClose }: { orderId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery<Detail>({
    queryKey: ['admin', 'order', orderId],
    queryFn: async () => (await api.get(`/admin/orders/${orderId}`)).data,
  });
  const advance = useMutation({
    mutationFn: async (to: string) => (await api.post(`/admin/orders/${orderId}/advance`, { to })).data,
    onSuccess: () => {
      toast.success('Order advanced.');
      qc.invalidateQueries({ queryKey: ['admin', 'order', orderId] });
      qc.invalidateQueries({ queryKey: ['admin', 'orders'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Could not advance the order.'),
  });

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className="card p-0 max-w-2xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="section-dark sticky top-0 bg-ink text-cream px-5 py-4 flex items-center justify-between z-10">
          <div>
            <div className="stamp text-gold text-[9px]">ORDER {data?.reference}</div>
            <h2 className="display text-xl mt-0.5">{data?.brand} {data?.productTitle}</h2>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-cream/10 hover:bg-cream/20 grid place-items-center text-cream text-lg">×</button>
        </div>

        {isLoading || !data ? (
          <div className="p-6 space-y-3">{[0, 1, 2, 3].map((i) => <div key={i} className="h-10 skeleton rounded" />)}</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Parties + money */}
            <div className="grid sm:grid-cols-3 gap-3 text-[13px]">
              <Box k="Buyer" v={data.buyer.name} sub={data.buyer.email} />
              <Box k="Seller" v={data.seller.name} sub={data.seller.email} />
              <Box k="Total / seller gets" v={`£${data.totalGBP.toFixed(2)}`} sub={`£${data.amountGBP.toFixed(2)} to seller`} />
            </div>

            {/* Addresses */}
            {(fmtAddr(data.shippingAddress) || fmtAddr(data.billingAddress)) && (
              <div className="grid sm:grid-cols-2 gap-3 text-[12px]">
                <div className="rounded-luxe border border-warmline p-3">
                  <div className="stamp text-gold-dark text-[9px] mb-1">DELIVERY ADDRESS</div>
                  {fmtAddr(data.shippingAddress) ?? <span className="text-mute">Locker / not provided</span>}
                  {data.shippingAddress?.phone && <div className="text-mute mt-0.5">📞 {data.shippingAddress.phone}</div>}
                </div>
                <div className="rounded-luxe border border-warmline p-3">
                  <div className="stamp text-gold-dark text-[9px] mb-1">BILLING ADDRESS</div>
                  {fmtAddr(data.billingAddress) ?? <span className="text-mute">Same as delivery / not provided</span>}
                </div>
              </div>
            )}

            {/* Lifecycle control */}
            <div>
              <div className="stamp text-gold-dark text-[9px] mb-2">LIFECYCLE · CURRENT: {data.state.replace(/_/g, ' ')}</div>
              <div className="grid sm:grid-cols-2 gap-2">
                {STEPS.map((s) => {
                  const enabled = s.from.includes(data.state);
                  const done = passed(data.state, s.to);
                  return (
                    <button key={s.to} disabled={!enabled || advance.isPending} onClick={() => advance.mutate(s.to)}
                      className={`text-left text-[12px] px-3 py-2.5 rounded-luxe border transition ${
                        done ? 'border-ok/40 bg-ok/8 text-ok' : enabled ? 'border-gold bg-gold/8 text-ink hover:bg-gold/15' : 'border-warmline text-mute opacity-60'
                      }`}>
                      {done ? '✓ ' : ''}{s.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-mute mt-2">Use these to walk an order through every phase when testing.</p>
            </div>

            {/* Delivery checkpoints */}
            <div>
              <div className="stamp text-gold-dark text-[9px] mb-2">DELIVERY · {data.carrier}{data.trackingCode ? ` · ${data.trackingCode}` : ''}</div>
              <ol className="relative border-l-2 border-warmline ml-1 space-y-3">
                {data.checkpoints.map((c) => (
                  <li key={c.key} className="ml-4 relative">
                    <span className={`absolute -left-[21px] top-0.5 w-3 h-3 rounded-full ${c.reached ? 'bg-ok' : c.current ? 'bg-gold animate-pulse' : 'bg-warmline'}`} />
                    <div className={`text-[12px] ${c.current ? 'font-semibold text-ink' : c.reached ? 'text-ink' : 'text-mute'}`}>{c.label}</div>
                    <div className="text-[10px] text-mute">{c.location}{c.at && ` · ${new Date(c.at).toLocaleString()}`}</div>
                  </li>
                ))}
              </ol>
            </div>

            {/* Evidence */}
            <div>
              <div className="stamp text-gold-dark text-[9px] mb-2">EVIDENCE &amp; PROOF</div>
              <div className="grid sm:grid-cols-2 gap-3">
                <Evidence title="Seller - serial / IMEI photo" note={data.evidence.imei ?? undefined} url={data.evidence.serialPhoto} />
                <Evidence title="Seller - proof of dispatch" url={data.evidence.dropoffPhoto} />
                <Evidence title="Buyer - collection photo" url={data.evidence.collectionPhoto} />
                {data.evidence.disputeReason && (
                  <div className="rounded-luxe border border-bad/30 bg-bad/5 p-3 sm:col-span-2">
                    <div className="stamp text-bad text-[9px] mb-1">DISPUTE · {data.evidence.disputeReason}</div>
                    {data.evidence.disputeNotes && <p className="text-[12px] text-ink-700">Buyer: “{data.evidence.disputeNotes}”</p>}
                    {data.evidence.sellerResponse && <p className="text-[12px] text-ink-700 mt-1">Seller: “{data.evidence.sellerResponse}”</p>}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {[...data.evidence.buyerDisputePhotos, ...data.evidence.sellerDisputePhotos].map((u, i) => <Thumb key={i} url={u} />)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Event log */}
            <div>
              <div className="stamp text-gold-dark text-[9px] mb-2">EVENT LOG</div>
              <ul className="text-[11px] text-mute space-y-1">
                {data.events.map((e, i) => (
                  <li key={i}><span className="mono text-ink">{e.toState}</span> · {e.source} · {new Date(e.at).toLocaleString()}{e.note ? ` - ${e.note}` : ''}</li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const RANK: Record<string, number> = { CREATED: 0, PAID_HELD: 1, AWAITING_DROP: 1, SHIPPED: 2, IN_LOCKER: 3, COLLECTED: 4, RELEASED: 5, DISPUTED: 3, REFUNDED: 5, CANCELLED: 0 };
const TO_RANK: Record<string, number> = { paid: 1, in_locker: 3, collected: 4, release: 5 };
function passed(state: string, to: string) { return (RANK[state] ?? 0) >= (TO_RANK[to] ?? 99); }

function Box({ k, v, sub }: { k: string; v: string; sub?: string }) {
  return (
    <div className="rounded-luxe border border-warmline p-3">
      <div className="stamp text-mute text-[9px]">{k}</div>
      <div className="font-medium truncate">{v}</div>
      {sub && <div className="text-[11px] text-mute truncate">{sub}</div>}
    </div>
  );
}

function Evidence({ title, url, note }: { title: string; url?: string; note?: string }) {
  return (
    <div className="rounded-luxe border border-warmline p-3">
      <div className="stamp text-mute text-[9px] mb-1.5">{title}</div>
      {url ? (
        <AuthedImage path={url} alt={title} className="w-full h-32 object-cover rounded-luxe border border-warmline" />
      ) : (
        <span className="text-[12px] text-mute">Not provided</span>
      )}
      {note && <div className="text-[10px] text-mute mt-1">{note}</div>}
    </div>
  );
}

function Thumb({ url }: { url: string }) {
  return <AuthedImage path={url} className="w-16 h-16 object-cover rounded border border-warmline" />;
}
